import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SUBJECTS_TYT, SUBJECTS_AYT } from "@/lib/constants";
import { printDetailedCard } from "@/lib/reportCard";
import { Loader2, Plus, Trash2, Printer, FileText, Download } from "lucide-react";

const netOf = (dc, yc) => (Number(dc || 0) - Number(yc || 0) / 4).toFixed(2);
const emptyInfo = { exam_type: "TYT", name: "", date: "", exam_code: "", booklet: "A", no: "", class_no: "", sube: "", puan: "", il: "", ilce: "", il_sr: "", ilce_sr: "" };
const rowFor = (subs) => subs.map((s) => ({ subject: s, ss: "", dc: "", yc: "", blank: "" }));

export default function DetailedExams({ studentId, readOnly }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [printId, setPrintId] = useState("");
  const [info, setInfo] = useState(emptyInfo);
  const [rows, setRows] = useState(rowFor(SUBJECTS_TYT));
  const [answers, setAnswers] = useState([]);
  const [branchDate, setBranchDate] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, st] = await Promise.all([api.get(`/detailed-exams/${studentId}`), api.get("/students").catch(() => ({ data: [] }))]);
      setItems(data);
      const me = (st.data || []).find((s) => s.id === studentId);
      if (me && (me.il || me.ilce)) setInfo((i) => ({ ...i, il: i.il || me.il || "", ilce: i.ilce || me.ilce || "" }));
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, [studentId]);
  useEffect(() => { if (studentId) load(); }, [load, studentId]);

  const setType = (t) => {
    setInfo((s) => ({ ...s, exam_type: t }));
    setRows(rowFor(t === "AYT" ? SUBJECTS_AYT : SUBJECTS_TYT));
  };
  const setRow = (i, f, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [f]: v } : r)));

  const fillFromBranch = async () => {
    if (!branchDate) return toast.error("Branş denemesi tarihi seçin");
    try {
      const { data } = await api.get(`/detailed-exams/${studentId}/from-branch/${branchDate}`);
      if (!data.subjects.length) return toast.error("Bu tarihte branş denemesi yok");
      setRows(data.subjects.map((s) => ({ subject: s.subject, ss: s.ss, dc: s.dc, yc: s.yc, blank: s.blank })));
      if (!info.date) setInfo((s) => ({ ...s, date: branchDate }));
      toast.success(`${data.subjects.length} branş dolduruldu`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const save = async () => {
    if (!info.name || !info.date) return toast.error("Sınav adı ve tarih zorunlu");
    const subjects = rows.filter((r) => r.subject && (r.dc !== "" || r.yc !== "" || r.blank !== "" || r.ss !== ""));
    if (!subjects.length) return toast.error("En az bir ders satırı doldurun");
    setSaving(true);
    try {
      await api.post(`/detailed-exams/${studentId}`, { ...info, puan: Number(info.puan || 0), subjects, answers: answers.filter((a) => a.key) });
      toast.success("Detaylı karne kaydedildi");
      setInfo(emptyInfo);
      setRows(rowFor(SUBJECTS_TYT));
      setAnswers([]);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/detailed-exams/${studentId}/${id}`); setItems((p) => p.filter((x) => x.id !== id)); toast.success("Silindi"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const printCard = async (id) => {
    setPrintId(id);
    try {
      const { data } = await api.get(`/detailed-exams/${studentId}/${id}/card`);
      const ok = printDetailedCard(data);
      if (!ok) toast.error("Karne penceresi açılamadı. Pop-up iznini kontrol edin.");
    } catch (e) { toast.error(apiErr(e)); } finally { setPrintId(""); }
  };

  return (
    <div className="space-y-5" data-testid="detailed-exams-panel">
      <div>
        <h3 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2"><FileText size={20} className="text-indigo-700" /> Detaylı Deneme Karnesi</h3>
        <p className="text-sm text-slate-500 mt-0.5">Ders bazında sonuç girin; Net, ortalamalar ve sıralamalar otomatik hesaplanır. Fotoğraftaki gibi detaylı karne olarak yazdırın.</p>
      </div>

      {!readOnly && (
        <div className="card-ta p-4 space-y-4" data-testid="det-form">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden w-fit">
            {["TYT", "AYT"].map((t) => (
              <button key={t} onClick={() => setType(t)} data-testid={`det-type-${t}`}
                className={`px-5 py-1.5 text-sm font-bold ${info.exam_type === t ? "bg-slate-900 text-white" : "text-slate-500"}`}>{t}</button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><label className="ta-label">Sınav Adı</label><input className="ta-input" value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} placeholder="TYT Branşlar Karması 5.Deneme" data-testid="det-name" /></div>
            <div><label className="ta-label">Sınav Tarihi</label><input type="date" className="ta-input" value={info.date} onChange={(e) => setInfo({ ...info, date: e.target.value })} data-testid="det-date" /></div>
            <div><label className="ta-label">Sınav Kodu</label><input className="ta-input" value={info.exam_code} onChange={(e) => setInfo({ ...info, exam_code: e.target.value })} data-testid="det-code" /></div>
            <div><label className="ta-label">Kitapçık Türü</label><input className="ta-input" value={info.booklet} onChange={(e) => setInfo({ ...info, booklet: e.target.value })} data-testid="det-booklet" /></div>
            <div><label className="ta-label">No</label><input className="ta-input" value={info.no} onChange={(e) => setInfo({ ...info, no: e.target.value })} data-testid="det-no" /></div>
            <div><label className="ta-label">Sınıf</label><input className="ta-input" value={info.class_no} onChange={(e) => setInfo({ ...info, class_no: e.target.value })} placeholder="11" data-testid="det-class" /></div>
            <div><label className="ta-label">Şube</label><input className="ta-input" value={info.sube} onChange={(e) => setInfo({ ...info, sube: e.target.value })} data-testid="det-sube" /></div>
            <div><label className="ta-label">Puan (opsiyonel)</label><input type="number" className="ta-input" value={info.puan} onChange={(e) => setInfo({ ...info, puan: e.target.value })} data-testid="det-puan" /></div>
            <div><label className="ta-label">İl</label><input className="ta-input" value={info.il} onChange={(e) => setInfo({ ...info, il: e.target.value })} placeholder="Örn: İstanbul" data-testid="det-il" /></div>
            <div><label className="ta-label">İlçe</label><input className="ta-input" value={info.ilce} onChange={(e) => setInfo({ ...info, ilce: e.target.value })} placeholder="Örn: Kadıköy" data-testid="det-ilce" /></div>
            <div><label className="ta-label">İl Sıralaması <span className="text-slate-400 font-normal">(boşsa otomatik)</span></label><input className="ta-input" value={info.il_sr} onChange={(e) => setInfo({ ...info, il_sr: e.target.value })} placeholder="Otomatik" data-testid="det-ilsr" /></div>
            <div><label className="ta-label">İlçe Sıralaması <span className="text-slate-400 font-normal">(boşsa otomatik)</span></label><input className="ta-input" value={info.ilce_sr} onChange={(e) => setInfo({ ...info, ilce_sr: e.target.value })} placeholder="Otomatik" data-testid="det-ilcesr" /></div>
          </div>

          <div className="flex items-end gap-2 border-t border-slate-100 pt-3">
            <div><label className="ta-label">Branş Denemesinden Doldur</label><input type="date" className="ta-input" value={branchDate} onChange={(e) => setBranchDate(e.target.value)} data-testid="det-branch-date" /></div>
            <Button variant="outline" onClick={fillFromBranch} className="border-emerald-300 text-emerald-700 h-10" data-testid="det-branch-fill"><Download size={15} /><span className="ml-1">Getir</span></Button>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="ta-label mb-0">Ders Bazında Sonuçlar</label>
              <button onClick={() => setRows((r) => [...r, { subject: "", ss: "", dc: "", yc: "", blank: "" }])} className="text-xs font-semibold text-indigo-600" data-testid="det-add-row">+ Ders</button>
            </div>
            <div className="hidden sm:grid grid-cols-[1.4fr_repeat(5,1fr)_28px] gap-2 px-1 mb-1 text-[11px] font-semibold text-slate-500">
              <span>Ders</span><span>Soru (SS)</span><span>Doğru</span><span>Yanlış</span><span>Boş</span><span>Net</span><span />
            </div>
            <div className="space-y-1.5">
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-[1.4fr_repeat(5,1fr)_28px] gap-2 items-center" data-testid={`det-row-${i}`}>
                  <input className="ta-input" value={r.subject} onChange={(e) => setRow(i, "subject", e.target.value)} placeholder="Ders" data-testid={`det-row-subject-${i}`} />
                  <input type="number" className="ta-input" value={r.ss} onChange={(e) => setRow(i, "ss", e.target.value)} data-testid={`det-row-ss-${i}`} />
                  <input type="number" className="ta-input" value={r.dc} onChange={(e) => setRow(i, "dc", e.target.value)} data-testid={`det-row-dc-${i}`} />
                  <input type="number" className="ta-input" value={r.yc} onChange={(e) => setRow(i, "yc", e.target.value)} data-testid={`det-row-yc-${i}`} />
                  <input type="number" className="ta-input" value={r.blank} onChange={(e) => setRow(i, "blank", e.target.value)} data-testid={`det-row-blank-${i}`} />
                  <div className="ta-input bg-slate-50 text-indigo-700 font-mono font-bold flex items-center justify-center" data-testid={`det-row-net-${i}`}>{netOf(r.dc, r.yc)}</div>
                  <button onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} className="text-rose-400 hover:text-rose-600" data-testid={`det-row-del-${i}`}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <label className="ta-label mb-0">Cevap Analizleri (opsiyonel)</label>
              <button onClick={() => setAnswers((a) => [...a, { label: "", key: "", student: "" }])} className="text-xs font-semibold text-indigo-600" data-testid="det-add-answer">+ Cevap Bloğu</button>
            </div>
            <div className="space-y-2">
              {answers.map((a, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_2fr_28px] gap-2 items-center" data-testid={`det-ans-${i}`}>
                  <input className="ta-input" value={a.label} onChange={(e) => setAnswers((ans) => ans.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="TYT_Türkçe" data-testid={`det-ans-label-${i}`} />
                  <input className="ta-input font-mono" value={a.key} onChange={(e) => setAnswers((ans) => ans.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} placeholder="Doğru cevaplar: CEEDEA..." data-testid={`det-ans-key-${i}`} />
                  <input className="ta-input font-mono" value={a.student} onChange={(e) => setAnswers((ans) => ans.map((x, j) => (j === i ? { ...x, student: e.target.value } : x)))} placeholder="Öğrenci cevapları" data-testid={`det-ans-student-${i}`} />
                  <button onClick={() => setAnswers((ans) => ans.filter((_, j) => j !== i))} className="text-rose-400 hover:text-rose-600"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="bg-indigo-900 hover:bg-indigo-800 h-11" data-testid="det-save">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}<span className="ml-1">Karneyi Kaydet</span>
          </Button>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : items.length === 0 ? (
        <div className="card-ta p-10 text-center text-slate-400">Henüz detaylı karne yok.</div>
      ) : (
        <div className="space-y-2" data-testid="det-list">
          {items.map((it) => (
            <div key={it.id} className="card-ta p-4 flex items-center gap-3" data-testid={`det-item-${it.id}`}>
              <span className={`text-xs font-extrabold px-2 py-0.5 rounded-full border ${it.exam_type === "AYT" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-blue-100 text-blue-700 border-blue-200"}`}>{it.exam_type}</span>
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-slate-900 truncate">{it.name || "-"}</div>
                <div className="text-[11px] text-slate-500">{it.date} · {(it.subjects || []).length} ders · Net {(it.subjects || []).reduce((a, s) => a + Number(s.net || 0), 0).toFixed(2)}</div>
              </div>
              <Button size="sm" onClick={() => printCard(it.id)} disabled={printId === it.id} className="h-8 bg-slate-900 hover:bg-slate-800 text-xs" data-testid={`det-print-${it.id}`}>
                {printId === it.id ? <Loader2 className="animate-spin" size={13} /> : <Printer size={13} />}<span className="ml-1">Karne Yazdır</span>
              </Button>
              {!readOnly && <button onClick={() => remove(it.id)} className="text-slate-300 hover:text-rose-600" data-testid={`det-del-${it.id}`}><Trash2 size={15} /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
